// Profile hooks — the WRAPPER layer (lint-exempt for raw TanStack Query). A person's
// profile is PERSON-scoped, never band-keyed: the own-profile keys live under ['me','profile'] so they
// survive a band switch (which scorches ['band', …]); viewing ANOTHER person is keyed ['person', personId,
// 'profile'] (a different realm than 'me', and never band-tenanted). Every write surfaces the server error
// verbatim and NEVER reports success on an unpersisted write — the modal catches and shows it
// inline (e.g. the skills exact-match 400).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { API_URL } from "../../config/env.js";
import { ApiError } from "./apiError.js";

type ProfileView = components["schemas"]["ProfileView"];
type PublicProfileView = components["schemas"]["PublicProfileView"];
type UpdateProfileRequest = components["schemas"]["UpdateProfileRequest"];
type UpdateTravelRequest = components["schemas"]["UpdateTravelRequest"];
type UpdatePaymentRequest = components["schemas"]["UpdatePaymentRequest"];

export type { ProfileView, PublicProfileView, UpdateProfileRequest, UpdateTravelRequest, UpdatePaymentRequest };

/** Own-profile cache key — person-scoped, survives a band switch. */
export const myProfileKey = ["me", "profile"] as const;
/** Another person's public profile key — a distinct realm from 'me', never band-tenanted. */
export function publicProfileKey(personId: string): readonly ["person", string, "profile"] {
  return ["person", personId, "profile"] as const;
}

/** `GET /me/profile` — the caller's editable profile incl. the travel block (edit-own preload).*/
export function useMyProfile() {
  const { api } = useApp();
  return useQuery({
    queryKey: myProfileKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/profile");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /users/{personId}/profile` — another person's PUBLIC profile (view-others; NO travel). A 404
 * surfaces as the query's error state so the modal can render the designed "not available" view.
 */
/**
 * `GET /users/{personId}/travel` — a crew member's Travel & Loyalty block.
 *
 * Its OWN endpoint, not a field on the profile: gated data never rides a shared shape here, so the public
 * profile stays travel-free by construction. The server answers 404 both when nothing is recorded... no —
 * when the viewer may not look. A viewer who MAY look gets an empty block instead, so the two states stay
 * distinguishable.
 *
 * Visible to the person themselves, or to a same-band viewer on the money team (AV owner, Tour Manager,
 * Management, Booking). A 404 here is the ORDINARY answer for everyone else, so the caller renders nothing
 * rather than an error — "you may not see this" is itself a disclosure, and there is no action to offer.
 * `retry: false` because a 404 is a verdict, not a blip.
 */
export function usePersonTravel(personId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: [...publicProfileKey(personId ?? "none"), "travel"],
    enabled: personId != null && personId !== "",
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/users/{personId}/travel", {
        params: { path: { personId: personId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /users/{personId}/payment` — a crew member's Venmo handle (ART-241). Its OWN endpoint on the money-team
 * gate, exactly like `/travel`: a viewer who MAY look gets a block (`venmoUsername` set or null); everyone else
 * gets a 404, which the caller renders as "no Payment section" rather than an error. `retry: false` — a 404 is
 * a verdict.
 */
export function usePersonPayment(personId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: [...publicProfileKey(personId ?? "none"), "payment"],
    enabled: personId != null && personId !== "",
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/users/{personId}/payment", {
        params: { path: { personId: personId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /users/{personId}/bands` — the bands the CALLER shares with this person, with the person's seat
 * role in each (the profile modal's band tiles). Self is the degenerate case: all your own bands. Keyed
 * under the person realm like the profile — the list is viewer-relative but person-addressed, and a band
 * switch doesn't change what the caller shares with someone.
 */
export function usePersonBands(personId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: [...publicProfileKey(personId ?? "none"), "bands"],
    enabled: personId != null && personId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/users/{personId}/bands", {
        params: { path: { personId: personId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.items ?? [];
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /users/{personId}/orgs` — the organizations the CALLER shares with this person, with the person's
 * org role in each (the profile modal's org tiles). The org analogue of {@link usePersonBands}, and what
 * gives an ORG staffer a rail at all: they hold no band seat, so the band strip is empty for them and the
 * profile read as "works with nobody".
 *
 * Keyed under the person realm like the profile and the bands read — viewer-relative but person-addressed,
 * and a band switch does not change which employers you share with someone.
 */
export function usePersonOrgs(personId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: [...publicProfileKey(personId ?? "none"), "orgs"],
    enabled: personId != null && personId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/users/{personId}/orgs", {
        params: { path: { personId: personId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.items ?? [];
    },
    staleTime: 60_000,
  });
}

export function usePublicProfile(personId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: publicProfileKey(personId ?? "none"),
    enabled: personId != null && personId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/users/{personId}/profile", {
        params: { path: { personId: personId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /skills` — the GLOBAL skill catalog (canonical casing, ordered): the profile editor's picker
 * source, and exactly the rows a save validates against. Effectively static — cache long.
 */
export function useSkillsCatalog(enabled = true) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["catalog", "skills"],
    enabled,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/skills");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.items ?? [];
    },
    staleTime: 60 * 60_000,
  });
}


/**
 * `PUT /me/avatar` — replace the caller's profile picture (multipart; jpeg/png/webp ≤ 5MB). Raw fetch:
 * the typed client is JSON-bodied. Reseeds the own-profile cache from the response (which carries the
 * NEW avatarMediaId) and invalidates the person-realm mirror so open profile surfaces repaint.
 */
export function useSetAvatar(personId: string | undefined) {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append("file", file);
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}/api/v1/me/avatar`, {
        method: "PUT",
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
        body,
      });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      return (await res.json()) as ProfileView;
    },
    onSuccess: (data) => {
      qc.setQueryData(myProfileKey, data);
      if (personId) void qc.invalidateQueries({ queryKey: publicProfileKey(personId) });
    },
  });
}

/** `PUT /me/profile` — name/phone/bio/location/positions[]/skills[]. Seeds the own-profile cache on success. */
export function useUpdateProfile() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateProfileRequest) => {
      const { data, error, response } = await api.PUT("/api/v1/me/profile", { body });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(myProfileKey, data);
    },
  });
}

/** `PUT /me/travel` — KTN / redress / loyalty. Returns the full ProfileView, so it reseeds the own-profile cache. */
export function useUpdateTravel() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateTravelRequest) => {
      const { data, error, response } = await api.PUT("/api/v1/me/travel", { body });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(myProfileKey, data);
    },
  });
}

/** `PUT /me/payment` — the Venmo handle (ART-241). Returns the full ProfileView, so it reseeds the own-profile
 *  cache; the caller also invalidates the person-scoped payment read so a manager's view refreshes. */
export function useUpdatePayment() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePaymentRequest) => {
      const { data, error, response } = await api.PUT("/api/v1/me/payment", { body });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(myProfileKey, data);
    },
  });
}
